import axios, { AxiosError, AxiosRequestHeaders } from 'axios';
import { normalizeApiRequestUrl } from './urlConfig';

export const AUTH_EXPIRED_EVENT = 'auth:expired';

const AUTH_EXCLUDED_PATHS = ['/login', '/token', '/logout', '/public/email-actions'];

const getTokenFromStorage = (): string | null => {
  const storedToken = sessionStorage.getItem('token');

  if (!storedToken) {
    return null;
  }

  try {
    return JSON.parse(storedToken);
  } catch {
    return storedToken;
  }
};

const isExcludedAuthPath = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return AUTH_EXCLUDED_PATHS.some((path) => url.includes(path));
};

const clearStoredAuth = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('roles');
};

export const apiClient = axios.create({
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = getTokenFromStorage();
  config.url = normalizeApiRequestUrl(config.url);
  const requestUrl = config.url;
  const headers = (config.headers ?? {}) as AxiosRequestHeaders;

  if (token && !isExcludedAuthPath(requestUrl)) {
    headers.Authorization = `Bearer ${token}`;
  } else if (isExcludedAuthPath(requestUrl) && headers.Authorization) {
    delete headers.Authorization;
  }

  config.headers = headers;

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url;

    if (status === 401 && !isExcludedAuthPath(requestUrl)) {
      clearStoredAuth();
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }

    return Promise.reject(error);
  },
);
